// import reports_v1 from "./reports_v1";
// import reports_v2 from "./reports_v2";

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { Buffer } from "buffer";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";

import { registerFonts } from "./reports_v2/utils/fonts";

import LoadingScreen from "./components/LoadingScreen";
import StatusScreen, { STATUS } from "./components/StatusScreen";
import { mockData, mockDataVariants } from "./reports_v2/mock-data";
import {
  BUILD_ENV,
  isLinkScannerRejection,
  isNetworkError,
  loadChunk,
  PDF_TYPES,
} from "./utils";
import { loadReport } from "./utils/load-report";
import {
  detectLanguage,
  looksStillValid,
  minutesSinceExpiry,
  parseReportLink,
} from "./utils/report-link";

const { version } = require("../package.json");

Bugsnag.start({
  apiKey: "a8d79e46a38c60bd828fdf5af45a7cac",
  plugins: [new BugsnagPluginReact()],
  releaseStage: BUILD_ENV,
  appVersion: version,
  onError: (event) => {
    if (process.env.NODE_ENV === "development") return false;

    // event.errors is an array of one or more Error objects.
    // The first item in the list represents the thrown object.
    // Each subsequent item represents the error that was
    // set as the cause of the preceding one.

    // An Error object contains the following information:
    // errorMessage /	String / The message string from the error
    // ...

    if (isNetworkError({ message: event.errors[0].errorMessage })) {
      return false;
    }

    if (
      isLinkScannerRejection(
        event.getMetadata("unhandledrejection handler", "non-error parameter")
      )
    ) {
      return false;
    }

    event.context = `version ${version}`;
    event.addMetadata("meta", {
      version,
      build_env: BUILD_ENV.toUpperCase(),
    });
  },
});

BugsnagPerformance.start({ apiKey: "a8d79e46a38c60bd828fdf5af45a7cac" });
const ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);

window.Buffer = Buffer;

const originalConsoleError = console.error;

const customConsoleError = (...args) => {
  originalConsoleError.apply(console, args);

  const [firstArgument] = args;

  if (firstArgument instanceof Error) {
    Bugsnag.notify(firstArgument);
  } else {
    Bugsnag.notify(new Error(args.join(" ")));
  }
};

console.error = customConsoleError;

/* Link und Sprache stehen fest, bevor irgendetwas geladen ist — und sie
   muessen auch dann noch da sein, wenn das Laden scheitert. Die Sprache kommt
   aus dem S3-Key im Link (s. utils/report-link.js); das JSON, das sie sonst
   traegt, gibt es bei einem abgelaufenen Link nicht mehr. */
const reportLink = parseReportLink(window.location.search);
const pageLanguage = detectLanguage({
  link: reportLink,
  navigatorLanguages: navigator.languages || [navigator.language],
});

/* Getrennt von reportLink: parseReportLink liefert auch dann null, wenn der
   Parameter zwar da, aber unlesbar ist. Der Unterschied zaehlt — ein blosser
   Aufruf von pdf.aivy.app ist Rauschen, ein verstuemmelter Parameter waere
   eine kaputte Integration. */
const hasDataParam = new URLSearchParams(window.location.search).has("data");

/* Was an Bugsnag geht, entscheidet sich hier und nicht in loadReport — die
   Funktion bleibt rein und testbar. Nie die signierte URL oder den S3-Key
   mitschicken: genau die Daten schuetzt die kurze Gueltigkeit. */
const reportOutcome = (outcome) => {
  if (outcome.status === "ok") return;

  if (outcome.status === STATUS.LINK_EXPIRED) {
    const minutes = minutesSinceExpiry(reportLink);

    /* Der Mensch davor sieht in beiden Faellen dieselbe Karte — er kann so oder
       so nur dort neu anfordern, wo er herkam. Getrennt wird nur, was im
       Posteingang landet (s. looksStillValid). */
    if (looksStillValid(reportLink)) {
      Bugsnag.notify(
        new Error("Report link rejected while still valid"),
        (event) => {
          event.severity = "warning";
          event.groupingHash = "report-link-forbidden-unexpired";
          event.addMetadata("link", {
            language: pageLanguage,
            minutes_since_expiry: minutes,
          });
        },
      );

      return;
    }

    /* Erwartet, kein Fehler: Links gelten bewusst nur 15 Minuten. Als Info mit
       fester Gruppierung gemeldet, damit die Haeufigkeit sichtbar bleibt, ohne
       im Posteingang wie ein Ausfall auszusehen. minutes_since_expiry trennt
       "Tab kurz liegen gelassen" von "Lesezeichen von letzter Woche". */
    Bugsnag.notify(new Error("Report link expired"), (event) => {
      event.severity = "info";
      event.groupingHash = "report-link-expired";
      event.addMetadata("link", {
        language: pageLanguage,
        minutes_since_expiry: minutes,
      });
    });

    return;
  }

  Bugsnag.notify(
    outcome.error || new Error(`Report could not be loaded (${outcome.cause})`),
    (event) => {
      event.addMetadata("link", {
        cause: outcome.cause,
        language: pageLanguage,
        /* has_data_param trennt den blossen Aufruf von pdf.aivy.app von einem
           Parameter, den die Seite nicht lesen konnte. */
        has_data_param: hasDataParam,
        has_link: Boolean(reportLink),
      });
    },
  );
};

/* Ohne FallbackComponent rendert die Bugsnag-ErrorBoundary null: eine leere
   Seite, und fuer pdfGenerator ein window.pdfurl, das nie kommt. Fuer den
   Menschen davor ist die Statusseite wenigstens eine Antwort. */
const RenderFailed = () => (
  <StatusScreen reason={STATUS.APP_FAILED} language={pageLanguage} />
);

/* ?mock=<key> in der Entwicklung, analog zu mockDataVariants: zeigt die
   Statusseite, ohne dafuer einen abgelaufenen Link zu brauchen. */
const STATUS_MOCKS = {
  "link-expired": { reason: STATUS.LINK_EXPIRED, language: "de" },
  "link-expired-en": { reason: STATUS.LINK_EXPIRED, language: "en" },
  "app-failed": { reason: STATUS.APP_FAILED, language: "de" },
  "app-failed-en": { reason: STATUS.APP_FAILED, language: "en" },
};

const Root = () => {
  const [IndexV1, setIndexV1] = useState(null);
  const [IndexV2, setIndexV2] = useState(null);

  const [dataV1, setDataV1] = useState(null);
  const [dataV2, setDataV2] = useState(null);
  const [type, setType] = useState();
  const [isLoading, setIsLoading] = useState(true);

  /* { reason, language } — der erste Fehler gewinnt. "Link abgelaufen" ist die
     praezisere Nachricht als "Report konnte nicht erstellt werden"; ein
     spaeterer Fehler beim Laden der Report-Chunks darf sie nicht
     ueberschreiben. */
  const [status, setStatus] = useState(null);

  const failWith = (reason, language = pageLanguage) =>
    setStatus((current) => current || { reason, language });

  console.log("Root component mounted");
  console.log("isLoading:", isLoading);

  useEffect(() => {
    async function init() {
      /* Liefert "ok" oder den STATUS-Wert, mit dem die Seite endet. Den
         Fehlerzustand setzt init() — sonst liefe der Import der Report-Chunks
         auch noch fuer eine Seite, die ohnehin nur eine Meldung zeigt. */
      async function fetchDataFromUrl() {
        /* Mit ?data= laeuft auch lokal der echte Weg — so laesst sich ein
           Link aus Produktion oder ein abgelaufener nachstellen, ohne zu
           bauen. Ohne data: Fixtures.

           Die Weiche haengt am Parameter, nicht am geparsten Link: sonst faellt
           ein verstuemmeltes ?data= lokal still auf eine heile Fixture zurueck
           und sieht aus, als waere der Link in Ordnung. */
        if (process.env.NODE_ENV === "development" && !hasDataParam) {
          // ?mock=<key> schaltet Fixture UND PDF-Typ um, ohne diese Datei
          // anzufassen (siehe mockDataVariants). Ohne Parameter bleibt es beim
          // Default-Typ in der Zeile darunter.
          const variant = new URLSearchParams(window.location.search).get(
            "mock",
          );

          const statusMock = STATUS_MOCKS[variant];
          if (statusMock) {
            failWith(statusMock.reason, statusMock.language);

            return statusMock.reason;
          }

          // Die v1-Fixtures liegen bei reports_v1 und werden nur hier geladen:
          // ein statischer Import zoege die 9000 Zeilen in das Prod-Bundle.
          const { mockDataVariantsV1 } = await loadChunk(
            () => import("./reports_v1/mock-data"),
          );
          const pickedV1 = mockDataVariantsV1[variant];

          // ?mock=v1-… rendert die alte Generation (in Produktion entscheidet
          // das career.diagnostic_version bzw. show_old_ui, s. unten).
          if (pickedV1) {
            setDataV1(pickedV1.data);
            setType(pickedV1.type);

            return "ok";
          }

          const picked = mockDataVariants[variant];

          const t = picked?.type || PDF_TYPES.MATCHING_PROFILE;
          const d = picked?.data || mockData[t];

          setDataV2(d);
          setType(t);

          return "ok";
        }

        const outcome = await loadReport({ link: reportLink });
        reportOutcome(outcome);

        if (outcome.status !== "ok") {
          /* Sofort und nicht erst nach dem Promise.all in init(): die Fonts
             darin sind 2,3 MB, die diese Seite nicht braucht. Sonst steht der
             Spinner noch Sekunden, obwohl die Antwort schon da ist. */
          failWith(outcome.status);

          return outcome.status;
        }

        if (outcome.isV1) setDataV1(outcome.data);
        else setDataV2(outcome.data);

        setType(outcome.type);

        return "ok";
      }

      async function importDynamicCompontens() {
        const [{ default: V1 }, { default: V2 }] = await Promise.all([
          loadChunk(() => import("./reports_v1")),
          loadChunk(() => import("./reports_v2")),
        ]);

        console.log("comps");
        setIndexV1(() => V1);
        setIndexV2(() => V2);
      }

      try {
        const [outcome] = await Promise.all([
          fetchDataFromUrl(),
          registerFonts(),
        ]);

        if (outcome !== "ok") {
          failWith(outcome);

          return;
        }

        // import components after the fonts are fetched!
        await importDynamicCompontens();
      } catch (err) {
        /* loadChunk hat Retries und einen Reload schon versucht — landet der
           Fehler hier, bleibt er bestehen. Ohne diesen Catch wuerde isLoading
           nie false werden und der Nutzer haengt ewig im LoadingScreen (und
           pdfGenerator im Timeout, s. loadChunk in ./utils). */
        Bugsnag.notify(err);
        failWith(STATUS.APP_FAILED);

        return;
      }

      setIsLoading(false);
    }

    init();
  }, []);

  if (status) {
    return <StatusScreen reason={status.reason} language={status.language} />;
  }

  if (isLoading) {
    /* Dieselbe Anzeige, die als HTML-Vorlage in public/index.html steht — die
       rendert, bevor dieses Bundle geladen ist, und wird hier uebernommen,
       damit beim Uebergang nichts blitzt. Weiter unten in der PdfShell kommt
       sie noch einmal, dort als inline-Variante unter der Kopfzeile. */
    return <LoadingScreen />;
  }

  // Handle missing data
  if (!dataV1 && !dataV2) {
    const stringified = JSON.stringify({ dataV1, dataV2, type });
    Bugsnag.notify(new Error(`No data or type provided / ${stringified}`));

    return <StatusScreen reason={STATUS.APP_FAILED} language={pageLanguage} />;
  }

  return dataV1 ? (
    <IndexV1 {...{ data: dataV1, type }} />
  ) : (
    <IndexV2 {...{ data: dataV2, type }} />
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={RenderFailed}>
      <Root />
    </ErrorBoundary>
  </React.StrictMode>,
);
